import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Space, Tag, Typography } from 'antd';
import { hasGraphQLErrorCode } from '../../utils/graphqlErrors';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { rowLabel } from '../diaryCard/model';
import {
  CHECK_IN_DRIVER_LABEL_KEYS,
  RECAP_FIELDS,
  type RecapRow,
} from './model';
// E43 — the phone's averages since the last session (Q-e43g1-6 A)
import { CheckInPhoneRecap } from '../health/CheckInPhoneRows';

/**
 * E37 · CheckInsSinceLastSession (buildOrder 7) — "Session form — Check-in recap": the session form's recap beside
 * E34's Review homework. Since the previous session — how many check-ins, the mood average and the lowest day with
 * its note, sleep, the drivers that came up most. Blueprint opens every session with this. The lowest day links to
 * the entry in the hub. A client with no earlier session: the last 14 days, and it says so (Q-e37g1-3 B). Entries the
 * practice entered in session are counted and marked; another practice's never appear. The diary-card highlights of
 * THIS practice's card (block B) follow — never another practice's, and not the client's own card. E43 — the phone's
 * average sleep and active minutes for the types the client shares with this therapist: `CheckInPhoneRecap`.
 */
type Props = {
  clientId: string | undefined;
  /** The session being written — its edge id; absent while the form has not saved it. */
  sessionId: string | undefined;
  locale: 'en' | 'ru';
};

export const CheckInSessionPanel = ({ clientId, sessionId, locale }: Props) => {
  const t = useTranslate();
  const { data, isError, error } = useCustom<RecapRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'checkInsSinceLastSession',
      fields: RECAP_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        sessionId: { name: 'sessionId', type: 'ID', value: sessionId || null },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const recap = data?.data;
  const dateLocale = locale === 'ru' ? ru : enUS;
  const day = (iso: string) =>
    format(parseISO(iso), 'd MMM yyyy', { locale: dateLocale });

  if (!clientId) {
    return (
      <Typography.Paragraph
        type="secondary"
        data-testid="checkin-session-pick-client"
      >
        {t('homework.session.pickClient')}
      </Typography.Paragraph>
    );
  }

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%' }}
      data-testid="checkin-recap-panel"
    >
      <Typography.Title level={5} style={{ margin: 0 }}>
        {t('checkIn.session.recapTitle')}
      </Typography.Title>
      {/* the recap is the client's own check-ins: a client who does not let this therapist VIEW their work is
          refused (HOMEWORK_NOT_YOUR_CLIENT, 2026-10-11) — said as the hub says it, not as a failure */}
      {isError && hasGraphQLErrorCode(error, 'HOMEWORK_NOT_YOUR_CLIENT') ? (
        <Typography.Paragraph
          type="secondary"
          data-testid="checkin-recap-not-shared"
        >
          {t('pages.clientsShow.notShared')}
        </Typography.Paragraph>
      ) : isError ? (
        <Alert type="error" showIcon message={t('checkIn.session.loadError')} />
      ) : null}
      {recap ? (
        <>
          <Typography.Text
            type="secondary"
            data-testid="checkin-recap-boundary"
          >
            {recap.firstSession
              ? t('checkIn.session.recapFirstSession')
              : t('checkIn.session.recapSince', {
                  date: recap.since ? day(recap.since) : '—',
                })}
          </Typography.Text>
          {recap.checkIns === 0 ? (
            <Typography.Paragraph
              type="secondary"
              data-testid="checkin-recap-empty"
            >
              {t('checkIn.session.recapEmpty')}
            </Typography.Paragraph>
          ) : (
            <Space direction="vertical" size={4}>
              <Typography.Text strong data-testid="checkin-recap-count">
                {t('checkIn.session.recapCount', { count: recap.checkIns })}
                {recap.inSessionCheckIns > 0
                  ? ` · ${t('checkIn.session.recapInSession', {
                      count: recap.inSessionCheckIns,
                    })}`
                  : ''}
              </Typography.Text>
              {recap.moodAverage != null ? (
                <span data-testid="checkin-recap-mood-average">
                  {t('checkIn.session.recapMoodAverage', {
                    value: recap.moodAverage,
                  })}
                </span>
              ) : null}
              {recap.moodLowest ? (
                <span data-testid="checkin-recap-lowest">
                  <Link
                    to={`/clients/show/${encodeURIComponent(
                      clientId,
                    )}?tab=checkins`}
                    data-testid="checkin-recap-lowest-link"
                  >
                    {t('checkIn.session.recapLowest', {
                      day: day(recap.moodLowest.day),
                      mood: recap.moodLowest.mood,
                    })}
                  </Link>
                  {recap.moodLowest.note ? ` — ${recap.moodLowest.note}` : ''}
                </span>
              ) : null}
              {recap.sleepAverageHours != null ? (
                <span data-testid="checkin-recap-sleep">
                  {t('checkIn.session.recapSleep', {
                    hours: recap.sleepAverageHours,
                  })}
                </span>
              ) : null}
              {recap.topDrivers.length ? (
                <span data-testid="checkin-recap-drivers">
                  {t('checkIn.session.recapDrivers')}:{' '}
                  {recap.topDrivers.map((d) => (
                    <Tag key={d.driver}>
                      {t(
                        CHECK_IN_DRIVER_LABEL_KEYS[
                          d.driver as keyof typeof CHECK_IN_DRIVER_LABEL_KEYS
                        ] ?? d.driver,
                      )}{' '}
                      ×{d.count}
                    </Tag>
                  ))}
                </span>
              ) : null}
            </Space>
          )}
          {recap.diaryHighlights ? (
            <Space
              direction="vertical"
              size={4}
              data-testid="checkin-recap-diary"
            >
              <Typography.Text strong>
                {t('diaryCard.session.recapTitle')}
              </Typography.Text>
              {recap.diaryHighlights.highlights.length === 0 ? (
                <Typography.Text
                  type="secondary"
                  data-testid="checkin-recap-diary-empty"
                >
                  {t('diaryCard.session.recapNone')}
                </Typography.Text>
              ) : (
                recap.diaryHighlights.highlights.map((h) => (
                  <span
                    key={`${h.day}-${h.targetId}`}
                    data-testid={`checkin-recap-diary-${h.targetId}-${h.day}`}
                  >
                    {t('diaryCard.session.recapLine', {
                      day: day(h.day),
                      name: rowLabel(t, h.targetId, h.name),
                      value:
                        h.urge != null
                          ? h.urge
                          : h.count != null
                          ? h.count
                          : t('diaryCard.hub.yes'),
                    })}
                  </span>
                ))
              )}
              {recap.diaryHighlights.skillsUsedAverage != null ? (
                <span data-testid="checkin-recap-diary-skills">
                  {t('diaryCard.session.recapSkills', {
                    value: recap.diaryHighlights.skillsUsedAverage,
                  })}
                </span>
              ) : null}
            </Space>
          ) : null}
          {/* E43 — the phone since the last session */}
          <CheckInPhoneRecap phone={recap} />
        </>
      ) : null}
    </Space>
  );
};
